import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Today — the home landing screen. One aggregated fetch (/users/me/today)
// powers a greeting, four headline metric cards, today's timeline, and a
// side column with "needs scheduling", goals, and a notifications nudge.
// It assembles data the app already owns; the goal is to give everyone a
// single screen to start (and re-check) their day from.
import { notify } from "../components/Toast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../hooks/useAuth";
// Browser timezone — sent to the backend so "today" is the user's local
// day, not a UTC day (a 9pm block should sit on tonight, not tomorrow).
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
function greeting() {
    const h = new Date().getHours();
    if (h < 12)
        return "Good morning";
    if (h < 18)
        return "Good afternoon";
    return "Good evening";
}
// ISO date → sortable number; null/blank sinks to the end.
function dateKey(iso) {
    if (!iso)
        return Number.POSITIVE_INFINITY;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}
function fmtDue(iso) {
    if (!iso)
        return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function NeedsSchedulingCard({ killList, mgmtAssigned, onScheduled, }) {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("all");
    const [busy, setBusy] = useState(false);
    const rows = useMemo(() => {
        const fromKill = killList.map((k) => ({
            id: k.id,
            title: k.title,
            project_id: k.project_id ?? null,
            project_name: k.project_name ?? null,
            tag: "kill list",
            task_id: k.task_id ?? null,
            task_due_date: k.task_due_date ?? null,
            task_order_index: k.task_order_index ?? 0,
            project_priority: k.project_priority ?? 5,
            project_due_date: k.project_due_date ?? null,
            is_one_off: !!k.is_one_off,
            readiness: k.readiness ?? "available",
            blocked_by: k.blocked_by ?? [],
        }));
        const fromMgmt = mgmtAssigned.map((m) => ({
            id: m.id,
            title: m.title,
            project_id: m.project_id ?? null,
            project_name: m.project_name ?? null,
            tag: "assigned to you",
            task_id: m.task_id ?? null,
            task_due_date: m.task_due_date ?? null,
            task_order_index: m.task_order_index ?? 0,
            project_priority: m.project_priority ?? 5,
            project_due_date: m.project_due_date ?? null,
            is_one_off: !!m.is_one_off,
            readiness: m.readiness ?? "available",
            blocked_by: m.blocked_by ?? [],
        }));
        const all = [...fromKill, ...fromMgmt];
        // Hide tasks blocked by an unresolved dependency ("waiting") — you
        // can't start them yet, so they only add noise. Exception: a blocked
        // task that's already PAST its due date stays visible (and flagged) so
        // an overdue-but-stuck item never silently disappears.
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayMs = todayStart.getTime();
        return all.filter((r) => {
            if (r.readiness !== "waiting")
                return true;
            // Keep a blocked task only if the TASK ITSELF is past due — not its
            // project. Many active projects are themselves overdue, and keying
            // the exception on the project date kept every blocked task in those
            // projects visible (the reported "didn't work"). A blocked task with
            // no task due date is hidden until its dependency clears.
            return dateKey(r.task_due_date) < todayMs;
        });
    }, [killList, mgmtAssigned]);
    function rowReadinessNote(r) {
        if (r.readiness !== "waiting")
            return null;
        return (_jsxs("div", { className: "today-needs-blocked muted small", children: ["\u23F3 waiting on", r.blocked_by.length > 0 ? `: ${r.blocked_by.join(", ")}` : " an earlier step"] }));
    }
    // ── Urgency classification: overdue → due this week → later ──
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayMs = todayStart.getTime();
    const weekMs = todayMs + 7 * 86400000;
    // Effective due = the sooner of the task's own due date and its project's.
    const effDue = (r) => Math.min(dateKey(r.task_due_date), dateKey(r.project_due_date));
    const urgencyOf = (r) => {
        const d = effDue(r);
        if (d === Number.POSITIVE_INFINITY)
            return "later";
        if (d < todayMs)
            return "overdue";
        if (d <= weekMs)
            return "week";
        return "later";
    };
    const counts = { overdue: 0, week: 0, later: 0 };
    for (const r of rows)
        counts[urgencyOf(r)] += 1;
    const visible = rows
        .filter((r) => (filter === "all" ? true : urgencyOf(r) === filter))
        .sort((a, b) => {
        // Blocked ("waiting") tasks sink to the bottom — can't start them yet.
        const aw = a.readiness === "waiting" ? 1 : 0;
        const bw = b.readiness === "waiting" ? 1 : 0;
        if (aw !== bw)
            return aw - bw;
        const da = effDue(a);
        const db = effDue(b);
        if (da !== db)
            return da - db;
        if (a.project_priority !== b.project_priority)
            return a.project_priority - b.project_priority;
        return a.title.localeCompare(b.title);
    });
    // ── Quick-schedule: drop a 30-min Deep Focus block today/tomorrow ──
    // Rows carry a task_id, which the block POST uses to flip the task to
    // in_progress and wipe matching kill-list rows — so after a refetch the
    // row drops off this list. Rows with no task_id (or an overlap 409) fall
    // back to opening the full scheduler so the user can place it by hand.
    function blockStart(when) {
        if (when === "tomorrow") {
            const s = new Date();
            s.setDate(s.getDate() + 1);
            s.setHours(9, 0, 0, 0); // 9am tomorrow — a sane default slot
            return s;
        }
        const s = new Date();
        s.setSeconds(0, 0);
        const add = (15 - (s.getMinutes() % 15)) % 15; // round up to next :15
        s.setMinutes(s.getMinutes() + add);
        return s;
    }
    function openScheduler() {
        navigate("/calendar", { state: { openScheduler: true } });
    }
    async function postBlock(r, when) {
        if (!r.task_id)
            return false;
        const start = blockStart(when);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + 30);
        await api.post("/time-blocks", {
            title: r.project_name ? `${r.project_name} — ${r.title}` : r.title,
            description: null,
            start_at: start.toISOString(),
            end_at: end.toISOString(),
            block_type: "lockout",
            is_personal: false,
            checklist_item_id: null,
            task_id: r.task_id,
            mirror_to_user_ids: [],
        });
        return true;
    }
    async function quickSchedule(r, when) {
        setBusy(true);
        try {
            const ok = await postBlock(r, when);
            if (!ok) {
                openScheduler();
                return;
            }
            await onScheduled();
        }
        catch {
            // Overlap / guard — let the user place it manually.
            openScheduler();
        }
        finally {
            setBusy(false);
        }
    }
    async function scheduleAllOverdue() {
        const overdue = rows.filter((r) => urgencyOf(r) === "overdue" && r.task_id);
        if (overdue.length === 0)
            return;
        setBusy(true);
        let placed = 0;
        // Stagger 30 min apart from the next quarter-hour so they don't collide.
        const base = blockStart("today");
        for (let i = 0; i < overdue.length; i++) {
            const start = new Date(base.getTime() + i * 30 * 60000);
            const end = new Date(start.getTime() + 30 * 60000);
            try {
                await api.post("/time-blocks", {
                    title: overdue[i].project_name
                        ? `${overdue[i].project_name} — ${overdue[i].title}`
                        : overdue[i].title,
                    description: null,
                    start_at: start.toISOString(),
                    end_at: end.toISOString(),
                    block_type: "lockout",
                    is_personal: false,
                    checklist_item_id: null,
                    task_id: overdue[i].task_id,
                    mirror_to_user_ids: [],
                });
                placed += 1;
            }
            catch {
                /* slot busy — skip; user can place the rest by hand */
            }
        }
        await onScheduled();
        setBusy(false);
        if (placed < overdue.length) {
            notify.error(`Scheduled ${placed} of ${overdue.length}. Some slots were busy — open the scheduler to place the rest.`);
        }
    }
    function rowActions(r) {
        const when = urgencyOf(r) === "overdue" ? "today" : "tomorrow";
        return (_jsxs("div", { className: "today-needs-actions", children: [_jsx("button", { type: "button", className: "today-quick-btn", disabled: busy, onClick: () => quickSchedule(r, when), title: `Drop a 30-min Deep Focus block ${when === "today" ? "today" : "tomorrow"}`, children: when === "today" ? "Today" : "Tmrw" }), _jsx("button", { type: "button", className: "today-schedule-btn", onClick: openScheduler, title: "Open the scheduler to place this exactly", children: "Schedule" })] }));
    }
    const header = (_jsxs("header", { className: "today-card-head today-needs-head", children: [_jsx("h2", { children: "Needs scheduling" }), _jsxs("div", { className: "today-needs-filter", role: "tablist", children: [_jsxs("button", { type: "button", className: filter === "all" ? "active" : "", onClick: () => setFilter("all"), children: ["All ", _jsx("span", { className: "today-needs-fcount", children: rows.length })] }), _jsxs("button", { type: "button", className: filter === "overdue" ? "active" : "", onClick: () => setFilter("overdue"), children: ["Overdue ", _jsx("span", { className: "today-needs-fcount", children: counts.overdue })] }), _jsxs("button", { type: "button", className: filter === "week" ? "active" : "", onClick: () => setFilter("week"), children: ["This week ", _jsx("span", { className: "today-needs-fcount", children: counts.week })] })] })] }));
    const banner = (_jsxs("div", { className: "today-needs-banner", children: [counts.overdue > 0 && (_jsxs("span", { className: "tnb tnb-red", children: [_jsx("i", {}), " ", counts.overdue, " overdue"] })), counts.week > 0 && (_jsxs("span", { className: "tnb tnb-amber", children: [_jsx("i", {}), " ", counts.week, " due this week"] })), counts.later > 0 && (_jsxs("span", { className: "tnb", children: [_jsx("i", {}), " ", counts.later, " later"] }))] }));
    if (rows.length === 0) {
        return (_jsxs("section", { className: "today-card tsec-needs", children: [header, _jsx("p", { className: "muted small today-side-empty", children: "Nothing waiting \u2014 everything's on the calendar." })] }));
    }
    return (_jsxs("section", { className: "today-card tsec-needs", children: [header, banner, _jsxs("ul", { className: "today-needs-list today-needs-scroll", children: [visible.map((r) => {
                        const due = fmtDue(r.task_due_date) ?? fmtDue(r.project_due_date);
                        const u = urgencyOf(r);
                        return (_jsxs("li", { className: `today-needs-row ${u === "overdue" ? "tnr-overdue" : u === "week" ? "tnr-week" : ""}`, children: [_jsxs("div", { className: "today-needs-title", children: [r.project_name && (_jsx("div", { className: "today-needs-project", children: r.project_name })), _jsx("div", { className: "today-needs-task", children: r.title }), _jsxs("div", { className: "today-needs-tag muted small", children: [r.tag, due ? ` · ${u === "overdue" ? "" : "due "}${due}` : "", ` · P${r.project_priority}`] }), rowReadinessNote(r)] }), rowActions(r)] }, r.id));
                    }), visible.length === 0 && (_jsx("li", { className: "muted small today-side-empty", children: "Nothing in this filter." }))] }), _jsxs("div", { className: "today-needs-foot", children: [counts.overdue > 0 && (_jsxs("button", { type: "button", className: "today-needs-bulk", disabled: busy, onClick: scheduleAllOverdue, children: ["\u26A1 Schedule ", counts.overdue, " overdue"] })), _jsxs("button", { type: "button", className: "today-needs-viewall", onClick: () => navigate("/projects?view=table&taskfilter=mine"), children: ["View all ", rows.length, " in My projects \u2192"] })] })] }));
}
// "07:00:00" / "07:00" → "7:00 am"
function fmtClock(t) {
    const parts = t.split(":");
    let h = parseInt(parts[0] ?? "0", 10);
    const m = parts[1] ?? "00";
    const ampm = h < 12 ? "am" : "pm";
    if (h === 0)
        h = 12;
    else if (h > 12)
        h -= 12;
    return `${h}:${m} ${ampm}`;
}
function fmtBlockTime(iso) {
    const d = new Date(iso);
    let h = d.getHours();
    const m = d.getMinutes().toString().padStart(2, "0");
    const ampm = h < 12 ? "am" : "pm";
    if (h === 0)
        h = 12;
    else if (h > 12)
        h -= 12;
    return `${h}:${m} ${ampm}`;
}
const BLOCK_LABEL = {
    lockout: "Deep Focus",
    flex: "Flex",
    personal: "Personal",
};
// Where each block sits relative to right now — drives the status pill
// and whether the timeline row is highlighted as "live".
function blockStatus(b) {
    if (b.is_completed)
        return { label: "done", live: false };
    const now = Date.now();
    const start = new Date(b.start_at).getTime();
    const end = new Date(b.end_at).getTime();
    if (now >= start && now < end)
        return { label: "in progress", live: true };
    if (now < start)
        return { label: "upcoming", live: false };
    // Past + not complete. If the user already hit "Not yet" (which
    // stamps reviewed_at), the work is underway but unfinished — show
    // "partially complete" instead of nagging "needs review". It flips
    // to "done" once is_completed (handled above) on full completion.
    if (b.reviewed_at)
        return { label: "partially complete", live: false };
    return { label: "needs review", live: false };
}
function fmtGoalDeadline(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    });
}
// Capitalize the cadence string ("monthly" → "Monthly"). Personal goals
// carry a null period so this never renders for them.
function fmtGoalPeriod(period) {
    if (!period)
        return null;
    return period.charAt(0).toUpperCase() + period.slice(1);
}
// Number of whole weeks that fall (at least partly) within the month
// containing `monthStartIso` — used to derive the weekly sub-target from a
// monthly milestone. Mon-first, matching the rest of the app.
function weeksInMonth(monthStartIso) {
    const [y, m] = monthStartIso.split("-").map(Number);
    const start = new Date(y, (m || 1) - 1, 1);
    const monthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    const firstMon = new Date(start);
    firstMon.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    let count = 0;
    const cur = new Date(firstMon);
    while (cur.getTime() <= monthEnd.getTime() && count < 6) {
        count += 1;
        cur.setDate(cur.getDate() + 7);
    }
    return Math.max(1, count);
}
// Milestone-driven status: judge a goal AHEAD / ON TRACK / BEHIND PLAN by
// comparing what's logged against the sum of past + current weekly/monthly
// milestone targets (current pro-rated by the elapsed fraction of the
// period). Returns null when the goal has no breakdown. Mirrors the same
// helper in Goals.tsx (kept in sync by hand; tiny + pure).
function milestoneStatusPill(goal) {
    if (!goal.milestone_period ||
        !goal.milestones ||
        goal.milestones.length === 0) {
        return null;
    }
    const ms = goal.milestones;
    const pastTarget = ms
        .filter((m) => m.is_past)
        .reduce((a, m) => a + m.target, 0);
    const pastActual = ms
        .filter((m) => m.is_past)
        .reduce((a, m) => a + m.actual, 0);
    const cur = ms.find((m) => m.is_current);
    let frac = 1;
    let curT = 0;
    let curA = 0;
    if (cur) {
        curT = cur.target;
        curA = cur.actual;
        const today = new Date();
        if (goal.milestone_period === "week") {
            const dow = (today.getDay() + 6) % 7; // Mon=0..Sun=6
            frac = (dow + 1) / 7;
        }
        else {
            const dim = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            frac = today.getDate() / dim;
        }
    }
    const expected = pastTarget + curT * frac;
    const actual = pastActual + curA;
    const cadence = goal.milestone_period === "week" ? "weekly" : "monthly";
    const r = (n) => Math.round(n * 10) / 10;
    const title = `Logged ${r(actual)} of ~${r(expected)} expected against your ${cadence} plan`;
    let st;
    if (expected <= 0)
        st = "ontrack";
    else if (actual >= expected * 1.05)
        st = "ahead";
    else if (actual < expected * 0.9)
        st = "behind";
    else
        st = "ontrack";
    if (st === "ahead")
        return { label: "AHEAD", cls: "ahead", title };
    if (st === "behind")
        return { label: "BEHIND PLAN", cls: "behind", title };
    return { label: "ON TRACK", cls: "on_pace", title };
}
// Build the Today milestone context for a full Goal — null when the goal has
// no breakdown or no current period. Derives the weekly sub-target for a
// monthly breakdown so the compact line can show a "this week" row, and the
// milestone-driven status for the row's pill.
function milestoneInfoFor(goal) {
    const period = goal.milestone_period;
    if (!period)
        return null;
    const current = (goal.milestones ?? []).find((m) => m.is_current);
    if (!current)
        return null;
    const status = milestoneStatusPill(goal);
    if (!status)
        return null;
    let weeklyTarget = null;
    if (period === "month" && current.target > 0) {
        const n = weeksInMonth(current.period_start);
        weeklyTarget = Math.max(1, Math.round(current.target / n));
    }
    return {
        period,
        current,
        weeklyTarget,
        thisWeekValue: goal.this_week_value ?? 0,
        unit: goal.unit?.trim() || "",
        status,
    };
}
function GoalRow({ g, ms }) {
    const pct = goalPct(g);
    const hasTarget = g.target_value != null;
    const buLabel = g.business_unit_name ?? null;
    const periodLabel = fmtGoalPeriod(g.period);
    const dueLabel = g.target_date ? fmtGoalDeadline(g.target_date) : null;
    // When the goal has a milestone breakdown, its milestone line is the single
    // source of truth — so the row's status pill follows the plan (ms.status)
    // and we suppress the legacy date-pace coaching (the expected-pace pin and
    // the catch-up "need X per period" block).
    const breakdown = !!ms;
    // BU goals show a metadata line: which BU + cadence + deadline.
    // Personal goals skip the line entirely.
    const showMeta = !!buLabel || !!periodLabel || !!dueLabel;
    // Expected-progress pin position on the bar (where the user
    // SHOULD be by today if they're meeting pace). Only renders when
    // we have a target and a backend-computed expected value — and the goal
    // has no breakdown (the milestone line replaces it).
    const expectedPct = !breakdown &&
        hasTarget &&
        typeof g.expected_value === "number" &&
        g.target_value &&
        g.target_value > 0
        ? (() => {
            // expected_value is absolute (baseline..target); position it along
            // the same span the fill uses so the pace pin lines up.
            const base = g.baseline_value ?? 0;
            const span = g.target_value - base;
            if (span <= 0)
                return null;
            return Math.min(100, Math.max(0, ((g.expected_value - base) / span) * 100));
        })()
        : null;
    // Catch-up only for non-breakdown goals — a breakdown goal's plan (the
    // milestone line) is the single source of truth.
    const isBehind = !breakdown &&
        (g.pace_state === "behind" || g.pace_state === "at_risk");
    return (_jsxs("div", { className: "today-goal", children: [_jsxs("div", { className: "today-goal-row", children: [_jsx("span", { className: "today-goal-title", children: g.title }), breakdown && ms && (_jsx("span", { className: `pill today-goal-pace ${ms.status.cls}`, title: ms.status.title, children: ms.status.label })), hasTarget && (_jsxs("span", { className: "muted small today-goal-value", children: [fmtNum(g.current_value), " /", " ", fmtNum(g.target_value), g.unit ? ` ${g.unit}` : ""] }))] }), showMeta && (_jsxs("div", { className: "today-goal-meta", children: [buLabel && (_jsx("span", { className: "pill today-goal-bu", children: buLabel })), periodLabel && (_jsx("span", { className: "muted small", children: periodLabel })), dueLabel && (_jsxs("span", { className: "muted small", children: ["Due ", dueLabel] })), !breakdown && g.pace_state && (_jsx("span", { className: `pill today-goal-pace ${g.pace_state}`, children: paceStateLabel(g.pace_state) }))] })), _jsxs("div", { className: "today-goal-bar", children: [_jsx("div", { className: `today-goal-fill ${pct >= 60 ? "ok" : "warn"}`, style: { width: `${pct}%` } }), expectedPct !== null && (_jsx("span", { className: "today-goal-expected-pin", style: { left: `${expectedPct}%` }, title: `Expected by today: ${fmtNum(g.expected_value)}${g.unit ? ` ${g.unit}` : ""}`, "aria-hidden": "true" }))] }), hasTarget && (_jsxs("span", { className: "muted small today-goal-pct", children: [pct, "% complete"] })), isBehind && g.catchup_per_period && g.catchup_period_unit && (_jsxs("div", { className: `today-goal-catchup ${g.pace_state === "at_risk" ? "risk" : ""}`, children: [_jsx("span", { className: "today-goal-catchup-label", children: g.pace_state === "at_risk"
                            ? "To still hit target"
                            : "To catch up" }), _jsxs("span", { className: "today-goal-catchup-body", children: [_jsx("strong", { children: fmtNum(g.catchup_per_period) }), g.unit ? ` ${g.unit}` : "", " ", "per ", g.catchup_period_unit, typeof g.days_left === "number" && g.days_left > 0 && (_jsxs("span", { className: "muted small", children: [" ", "\u00B7 ", g.days_left, " ", g.days_left === 1 ? "day" : "days", " left"] }))] })] })), ms && _jsx(GoalMilestoneLine, { g: g, ms: ms })] }));
}
// The Today compact milestone block: "This {month|week}: X / Y unit" with a
// bar, plus (for monthly) a "This week" sub-target line. Never scolding.
function GoalMilestoneLine({ g, ms }) {
    const unitSuffix = ms.unit ? ` ${ms.unit}` : "";
    const act = ms.current.actual;
    const tgt = ms.current.target;
    const pct = tgt > 0 ? Math.min(100, Math.round((act / tgt) * 100)) : 0;
    const periodLabel = ms.period === "month" ? "month" : "week";
    const periodAhead = tgt > 0 && act >= tgt;
    const weeklyTarget = ms.weeklyTarget;
    const wkAhead = weeklyTarget != null && ms.thisWeekValue >= weeklyTarget;
    const wkPct = weeklyTarget && weeklyTarget > 0
        ? Math.min(100, Math.round((ms.thisWeekValue / weeklyTarget) * 100))
        : 0;
    void g;
    return (_jsxs("div", { className: "today-goal-ms", children: [_jsxs("div", { className: "today-goal-ms-line", children: [_jsxs("span", { children: ["This ", periodLabel, periodAhead ? (_jsx("span", { className: "pill today-goal-ms-ahead", children: " ahead \uD83D\uDD25" })) : null] }), _jsxs("b", { children: [fmtNum(act), " / ", fmtNum(tgt), unitSuffix] })] }), _jsx("div", { className: "today-goal-ms-bar", children: _jsx("div", { className: `today-goal-ms-fill ${periodAhead ? "ok" : "or"}`, style: { width: `${pct}%` } }) }), weeklyTarget != null && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "today-goal-ms-line", children: [_jsxs("span", { children: ["This week", wkAhead ? (_jsx("span", { className: "pill today-goal-ms-ahead", children: " ahead \uD83D\uDD25" })) : null] }), _jsxs("b", { children: [fmtNum(ms.thisWeekValue), " / ", fmtNum(weeklyTarget), unitSuffix] })] }), _jsx("div", { className: "today-goal-ms-bar", children: _jsx("div", { className: `today-goal-ms-fill ${wkAhead ? "ok" : "or"}`, style: { width: `${wkPct}%` } }) })] }))] }));
}
function paceStateLabel(state) {
    switch (state) {
        case "ahead":
            return "Ahead";
        case "on_pace":
            return "On pace";
        case "behind":
            return "Behind";
        case "at_risk":
            return "At risk";
        default:
            return state;
    }
}
function goalPct(g) {
    if (g.target_value && g.target_value > 0) {
        // % growth goals progress along the span ABOVE the baseline, so 3/9 with
        // a baseline of 3 reads 0% (not 33%) — matching the Goals page.
        const base = g.baseline_value ?? 0;
        const span = g.target_value - base;
        if (span <= 0)
            return 0;
        return Math.min(100, Math.max(0, Math.round(((g.current_value - base) / span) * 100)));
    }
    return 0;
}
// "On track" / "Ahead" → positive; "Behind" → warning; else neutral.
function paceClass(pace) {
    if (pace === "Behind")
        return "warn";
    if (pace === "On track" || pace === "Ahead")
        return "ok";
    return "muted";
}
// Trim trailing-zero noise: 3 not "3.0", 1.5 stays "1.5".
function fmtNum(n) {
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
// Compact relative timestamp for the notifications list.
function fmtRelative(iso) {
    const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMin < 1)
        return "just now";
    if (diffMin < 60)
        return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24)
        return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7)
        return `${diffDay}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
}
// Sections the minimizer bar can collapse/expand. Order matches their
// rough top-to-bottom appearance so the bar reads like a table of contents.
const TODAY_SECTIONS = [
    { key: "timeline", label: "Timeline" },
    { key: "week", label: "Week recap" },
    { key: "needs", label: "Needs scheduling" },
    { key: "bottleneck", label: "Bottleneck radar" },
    { key: "goals", label: "Goals" },
    { key: "notifications", label: "Notifications" },
    { key: "team", label: "BU team" },
];
const TODAY_COLLAPSE_KEY = "today-collapsed-sections";
export default function TodayPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [feed, setFeed] = useState(null);
    // Which Today sections are minimized (collapsed to just their header).
    // Persisted so the user's decluttered layout survives reloads.
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const raw = localStorage.getItem(TODAY_COLLAPSE_KEY);
            return new Set(raw ? JSON.parse(raw) : []);
        }
        catch {
            return new Set();
        }
    });
    function persistCollapsed(next) {
        try {
            localStorage.setItem(TODAY_COLLAPSE_KEY, JSON.stringify([...next]));
        }
        catch {
            /* ignore quota / private-mode errors */
        }
    }
    function toggleSection(key) {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            persistCollapsed(next);
            return next;
        });
    }
    function setAllCollapsed(all) {
        const next = all
            ? new Set(TODAY_SECTIONS.map((s) => s.key))
            : new Set();
        persistCollapsed(next);
        setCollapsed(next);
    }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Notification list collapses to 5 by default — long noisy lists
    // drown out the rest of Today's chrome. "Show N more" expands it.
    const [showAllNotifs, setShowAllNotifs] = useState(false);
    // Today's timeline shows the first 8 blocks, then a "Show N more" toggle —
    // a packed day (30+ blocks) shouldn't push the rest of the page down.
    const [timelineExpanded, setTimelineExpanded] = useState(false);
    // Goal milestone breakdowns keyed by goal id. The Today feed's slim
    // TodayGoal doesn't carry the per-period breakdown, so we fetch the full
    // goals (/me/goals) and derive the current-period context per goal — used
    // to render the compact "this month/week" milestone line under each goal.
    const [milestoneMap, setMilestoneMap] = useState(new Map());
    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await api.get(`/users/me/today?tz=${encodeURIComponent(BROWSER_TZ)}`);
            setFeed(data);
            // Enrich with milestone breakdowns. Best-effort: a failure here just
            // means the compact milestone lines don't render — the rest of Today is
            // unaffected.
            try {
                const goals = await api.get("/me/goals");
                const next = new Map();
                for (const g of goals) {
                    const info = milestoneInfoFor(g);
                    if (info)
                        next.set(g.id, info);
                }
                setMilestoneMap(next);
            }
            catch {
                /* milestone enrichment is optional */
            }
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load your day");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        setLoading(true);
        void load();
    }, [load, user?.id]);
    // Refresh when the tab regains focus — the day moves on while it sits
    // open, and blocks may have been added on the calendar in the meantime.
    useEffect(() => {
        const onFocus = () => void load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [load]);
    if (!user)
        return null;
    const firstName = user.name.split(" ")[0] || user.name;
    const dateLabel = new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
    });
    return (_jsxs("div", { className: "page today-page", children: [_jsxs("header", { className: "today-head", children: [_jsxs("h1", { children: [greeting(), ", ", firstName] }), _jsxs("p", { className: "muted small", children: [dateLabel, feed && (_jsxs(_Fragment, { children: [" · awake ", fmtClock(feed.awake_start), "\u2013", fmtClock(feed.awake_end), feed.awake_is_overridden && (_jsx("span", { className: "pill ok today-awake-pill", children: "override" }))] }))] })] }), loading && _jsx("div", { className: "loading", children: "Loading your day\u2026" }), error && _jsx("div", { className: "error", children: error }), feed && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "today-metrics", children: [_jsxs("button", { type: "button", className: "today-metric", onClick: () => navigate("/calendar"), title: "Open your calendar", children: [_jsx("span", { className: "today-metric-label", children: "Blocks today" }), _jsx("span", { className: "today-metric-value", children: feed.blocks_count })] }), _jsxs("button", { type: "button", className: "today-metric", onClick: () => navigate("/projects"), title: "See tasks across your projects", children: [_jsx("span", { className: "today-metric-label", children: "Tasks due soon" }), _jsx("span", { className: "today-metric-value", children: feed.tasks_due_soon })] }), _jsxs("button", { type: "button", className: "today-metric", onClick: () => navigate("/calendar", { state: { openScheduler: true } }), title: "Schedule your unscheduled work", children: [_jsx("span", { className: "today-metric-label", children: "Unscheduled" }), _jsx("span", { className: "today-metric-value", children: feed.unscheduled_count })] }), _jsxs("button", { type: "button", className: "today-metric", onClick: () => navigate("/goals"), title: "Open your goals", children: [_jsx("span", { className: "today-metric-label", children: "Goal pace" }), _jsx("span", { className: `today-metric-value today-pace ${paceClass(feed.goal_pace)}`, children: feed.goal_pace })] })] }), _jsxs("div", { className: "today-minimizer", role: "toolbar", "aria-label": "Minimize Today sections", children: [_jsx("span", { className: "today-minimizer-label", children: "Sections" }), TODAY_SECTIONS.map((s) => {
                                const isCollapsed = collapsed.has(s.key);
                                return (_jsxs("button", { type: "button", className: `today-min-chip${isCollapsed ? " collapsed" : ""}`, "aria-pressed": !isCollapsed, onClick: () => toggleSection(s.key), title: isCollapsed ? `Expand ${s.label}` : `Minimize ${s.label}`, children: [_jsx("span", { className: "today-min-dot", "aria-hidden": "true", children: isCollapsed ? "+" : "−" }), s.label] }, s.key));
                            }), _jsx("button", { type: "button", className: "today-min-all", onClick: () => setAllCollapsed(collapsed.size < TODAY_SECTIONS.length), title: collapsed.size < TODAY_SECTIONS.length
                                    ? "Minimize every section"
                                    : "Expand every section", children: collapsed.size < TODAY_SECTIONS.length
                                    ? "Minimize all"
                                    : "Expand all" })] }), _jsxs("div", { className: `today-grid${TODAY_SECTIONS.filter((s) => collapsed.has(s.key))
                            .map((s) => ` collapse-${s.key}`)
                            .join("")}`, children: [_jsxs("div", { className: "today-col", children: [_jsxs("section", { className: "today-card today-timeline tsec-timeline", children: [_jsxs("header", { className: "today-card-head", children: [_jsx("h2", { children: "Today's timeline" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => navigate("/calendar"), children: "Open calendar" })] }), feed.blocks.length === 0 ? (_jsxs("div", { className: "today-empty", children: [_jsx("p", { children: "No blocks planned today." }), _jsx("p", { className: "muted small", children: "Time-blocking works best when the day is planned before it starts." }), _jsx("button", { type: "button", className: "btn-primary", onClick: () => navigate("/calendar"), children: "Add a block" })] })) : (_jsxs("ul", { className: "today-block-list", children: [(timelineExpanded
                                                        ? feed.blocks
                                                        : feed.blocks.slice(0, 8)).map((b) => {
                                                        const st = blockStatus(b);
                                                        return (_jsxs("li", { className: `today-block-row${st.live ? " is-live" : ""}`, onClick: () => navigate("/calendar"), children: [_jsx("span", { className: "today-block-time", children: st.live ? "now" : fmtBlockTime(b.start_at) }), _jsx("span", { className: `today-block-dot dot-${b.block_type}`, "aria-hidden": "true" }), _jsxs("span", { className: "today-block-title", children: [b.title, b.block_type !== "lockout" && (_jsxs("span", { className: "muted small today-block-kind", children: [" · ", BLOCK_LABEL[b.block_type] ?? b.block_type] }))] }), st.live ? (_jsx("button", { type: "button", className: "today-focus-btn", onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        navigate("/focus");
                                                                    }, children: "Start focus" })) : (_jsx("span", { className: "today-block-status", children: st.label }))] }, b.id));
                                                    }), feed.blocks.length > 8 && (_jsx("li", { className: "today-block-more", children: _jsx("button", { type: "button", className: "btn-link", onClick: () => setTimelineExpanded((v) => !v), children: timelineExpanded
                                                                ? "Show less"
                                                                : `Show ${feed.blocks.length - 8} more` }) }))] }))] }), feed.week_summary && (_jsx(TodayWeekSummaryCard, { summary: feed.week_summary, firstName: firstName })), _jsxs("section", { className: "today-card tsec-goals", children: [_jsxs("header", { className: "today-card-head", children: [_jsx("h2", { children: "Goals" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => navigate("/goals"), children: "All goals" })] }), feed.goals_achieved_this_week > 0 && (_jsxs("p", { className: "today-goals-achieved", children: [_jsx("span", { "aria-hidden": "true", children: "\uD83C\uDFC6" }), " ", feed.goals_achieved_this_week, " accomplished this week"] })), (() => {
                                                // Two sub-sections — personal commitments first, then
                                                // the BU-wide rollup. Older clients still see
                                                // feed.goals as the legacy personal slice.
                                                const personal = feed.personal_goals ?? feed.goals;
                                                const bu = feed.bu_goals ?? [];
                                                if (personal.length === 0 && bu.length === 0) {
                                                    return (_jsx("p", { className: "muted small today-side-empty", children: "No goals yet \u2014 set one to give your week a direction." }));
                                                }
                                                return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "today-goal-section", children: [_jsxs("p", { className: "today-goal-section-label", children: ["Personal goals", _jsxs("span", { className: "muted small", children: [" · ", personal.length] })] }), personal.length === 0 ? (_jsx("p", { className: "muted small today-side-empty", children: "None yet. Set a personal goal to track." })) : (_jsx("div", { className: "today-goal-list", children: personal.slice(0, 3).map((g) => (_jsx(GoalRow, { g: g, ms: milestoneMap.get(g.id) }, g.id))) }))] }), _jsxs("div", { className: "today-goal-section", children: [_jsxs("p", { className: "today-goal-section-label", children: ["Business unit goals", _jsxs("span", { className: "muted small", children: [" · ", bu.length] })] }), bu.length === 0 ? (_jsx("p", { className: "muted small today-side-empty", children: "No BU commitments set right now." })) : (_jsx("div", { className: "today-goal-list", children: bu.slice(0, 3).map((g) => (_jsx(GoalRow, { g: g, ms: milestoneMap.get(g.id) }, g.id))) }))] })] }));
                                            })()] })] }), _jsxs("div", { className: "today-col", children: [_jsx(NeedsSchedulingCard, { killList: feed.kill_list, mgmtAssigned: feed.mgmt_assigned, onScheduled: load }), _jsxs("section", { className: "today-card today-card-bottleneck tsec-bottleneck", children: [_jsx("header", { className: "today-card-head", children: _jsx("h2", { children: "\u26A0 Bottleneck radar" }) }), feed.past_due_projects.length === 0 &&
                                                feed.bottlenecks.length === 0 ? (_jsx("p", { className: "muted small today-side-empty", children: "Nothing flagged \u2014 projects on track and your tasks are unblocked." })) : (_jsx(_Fragment, {})), feed.past_due_projects.length > 0 && (_jsxs("div", { className: "today-bottleneck-section", children: [_jsxs("p", { className: "today-bottleneck-section-label", children: ["Past due projects", _jsxs("span", { className: "muted small", children: [" · ", feed.past_due_projects.length] })] }), _jsx("ul", { className: "today-bottleneck-list", children: feed.past_due_projects.slice(0, 5).map((p) => (_jsxs("li", { className: "today-bottleneck-row", onClick: () => navigate(`/projects?project=${p.id}`), children: [_jsxs("span", { className: "pill danger", children: [p.days_overdue, "d overdue"] }), _jsxs("span", { className: "today-bottleneck-title", children: [p.name, p.business_unit_name && (_jsxs("span", { className: "muted small", children: [" · ", p.business_unit_name] }))] })] }, p.id))) })] })), feed.bottlenecks.length > 0 && (_jsxs("div", { className: "today-bottleneck-section", children: [_jsxs("p", { className: "today-bottleneck-section-label", children: ["Your blocked or at-risk tasks", _jsxs("span", { className: "muted small", children: [" · ", feed.bottlenecks.length] })] }), _jsx("ul", { className: "today-bottleneck-list", children: feed.bottlenecks.slice(0, 5).map((b) => (_jsxs("li", { className: "today-bottleneck-row", onClick: () => navigate(b.project_id
                                                                ? `/projects?project=${b.project_id}`
                                                                : "/projects"), children: [_jsx("span", { className: `pill ${b.reason === "unowned" ? "" : "warn"}`, children: b.reason === "blocked"
                                                                        ? "blocked"
                                                                        : b.reason === "at_risk"
                                                                            ? "at risk"
                                                                            : "unowned" }), _jsxs("span", { className: "today-bottleneck-title", children: [b.task_title, b.project_name && (_jsxs("span", { className: "muted small", children: [" · ", b.project_name] }))] })] }, b.task_id))) })] }))] }), _jsxs("section", { className: "today-card tsec-notifications", children: [_jsxs("header", { className: "today-card-head", children: [_jsx("h2", { children: "Notifications" }), feed.unread_notifications > 0 && (_jsxs("span", { className: "today-notif-count", children: [feed.unread_notifications, " new"] }))] }), feed.notifications.length === 0 ? (_jsx("p", { className: "muted small today-side-empty", children: "No notifications yet." })) : (_jsxs("ul", { className: "today-notif-list", children: [(showAllNotifs
                                                        ? feed.notifications
                                                        : feed.notifications.slice(0, 5)).map((n) => {
                                                        // Nudges carry the sender's free-text note in the
                                                        // payload — surface it inline under the headline.
                                                        const note = n.type === "nudge" && n.payload
                                                            ? n.payload.note
                                                            : null;
                                                        return (_jsxs("li", { className: `today-notif-item${n.read_at ? "" : " is-unread"}`, children: [_jsx("span", { className: "today-notif-msg", children: n.message }), typeof note === "string" && note.trim() && (_jsxs("span", { className: "today-notif-note", children: ["\"", note, "\""] })), _jsx("span", { className: "muted small today-notif-time", children: fmtRelative(n.created_at) }), _jsx("button", { type: "button", className: "today-notif-dismiss", title: "Hide this notification", "aria-label": "Hide this notification", onClick: () => {
                                                                        // Optimistic remove. Snapshot the
                                                                        // old list so we can restore on a
                                                                        // server failure rather than leaving
                                                                        // the row in limbo.
                                                                        const prev = feed.notifications;
                                                                        setFeed((cur) => cur
                                                                            ? {
                                                                                ...cur,
                                                                                notifications: cur.notifications.filter((x) => x.id !== n.id),
                                                                                unread_notifications: n.read_at
                                                                                    ? cur.unread_notifications
                                                                                    : Math.max(0, cur.unread_notifications - 1),
                                                                            }
                                                                            : cur);
                                                                        void api
                                                                            .del(`/notifications/${n.id}`)
                                                                            .catch(() => {
                                                                            // Restore on failure.
                                                                            setFeed((cur) => cur
                                                                                ? {
                                                                                    ...cur,
                                                                                    notifications: prev,
                                                                                }
                                                                                : cur);
                                                                        });
                                                                    }, children: "\u00D7" })] }, n.id));
                                                    }), feed.notifications.length > 5 && (_jsx("li", { className: "today-notif-item today-notif-show-more", children: _jsx("button", { type: "button", className: "btn-link", onClick: () => setShowAllNotifs((v) => !v), children: showAllNotifs
                                                                ? "Show less"
                                                                : `Show ${feed.notifications.length - 5} more` }) }))] }))] }), feed.bu_team_today.length > 0 && (_jsxs("section", { className: "today-card today-card-full today-bu-team-card tsec-team", children: [_jsxs("header", { className: "today-card-head", children: [_jsx("h2", { children: "Your business units today" }), _jsxs("span", { className: "muted small", children: [feed.bu_team_today.length, " ", feed.bu_team_today.length === 1
                                                                ? "teammate"
                                                                : "teammates"] })] }), (() => {
                                                const byBu = new Map();
                                                for (const m of feed.bu_team_today) {
                                                    for (const b of m.blocks) {
                                                        const key = b.business_unit_name ?? "Unscoped";
                                                        const arr = byBu.get(key) ?? [];
                                                        arr.push({ who: m.user_name, block: b });
                                                        byBu.set(key, arr);
                                                    }
                                                }
                                                const buKeys = Array.from(byBu.keys()).sort();
                                                return (_jsx("div", { className: "today-bu-team-groups", children: buKeys.map((buKey) => {
                                                        const rows = byBu.get(buKey);
                                                        // Sort rows by activity then start time so blocks
                                                        // for the same activity cluster together.
                                                        rows.sort((a, b) => {
                                                            const aa = a.block.activity_name ?? "";
                                                            const ba = b.block.activity_name ?? "";
                                                            if (aa !== ba)
                                                                return aa.localeCompare(ba);
                                                            return a.block.start_at.localeCompare(b.block.start_at);
                                                        });
                                                        return (_jsxs("section", { className: "today-bu-team-group", children: [_jsxs("header", { className: "today-bu-team-group-head", children: [_jsx("strong", { children: buKey }), _jsxs("span", { className: "muted small", children: [rows.length, " ", rows.length === 1 ? "block" : "blocks"] })] }), _jsx("ul", { className: "today-bu-team-blocks", children: rows.map(({ who, block: b }, idx) => {
                                                                        const pid = b.project_id;
                                                                        const clickable = !!pid;
                                                                        return (_jsxs("li", { className: `today-bu-team-block${clickable
                                                                                ? " today-bu-team-block-clickable"
                                                                                : ""}`, onClick: clickable
                                                                                ? () => navigate(`/projects?project=${pid}`)
                                                                                : undefined, role: clickable ? "button" : undefined, tabIndex: clickable ? 0 : undefined, onKeyDown: clickable
                                                                                ? (e) => {
                                                                                    if (e.key === "Enter" ||
                                                                                        e.key === " ") {
                                                                                        e.preventDefault();
                                                                                        navigate(`/projects?project=${pid}`);
                                                                                    }
                                                                                }
                                                                                : undefined, title: clickable ? "Open project" : undefined, children: [_jsx("span", { className: `today-block-dot dot-${b.block_type}`, "aria-hidden": "true" }), _jsxs("span", { className: "today-bu-team-block-title", children: [b.task_title ?? b.title, b.activity_name && (_jsx("span", { className: "pill today-bu-activity-pill", children: b.activity_name })), _jsxs("span", { className: "muted small", children: [" · ", who, b.project_name
                                                                                                    ? ` · ${b.project_name}`
                                                                                                    : ""] })] }), _jsx("span", { className: "muted small today-bu-team-block-time", children: fmtBlockTime(b.start_at) })] }, idx));
                                                                    }) })] }, buKey));
                                                    }) }));
                                            })()] }))] })] })] }))] }));
}
// Glyph + accent color per momentum event type — keeps the row
// header readable at a glance without forcing the user to parse the
// title for context.
function weekEventGlyph(t) {
    switch (t) {
        case "project_completed":
            return "🚀";
        case "goal_achieved":
            return "🎯";
        case "streak_milestone":
            return "🔥";
        case "badge_earned":
            return "🏅";
        case "bottleneck_cleared":
            return "🔓";
        case "task_completed":
        default:
            return "✓";
    }
}
function weekEventCls(t) {
    switch (t) {
        case "goal_achieved":
            return "ev-goal";
        case "bottleneck_cleared":
            return "ev-bottleneck";
        case "project_completed":
            return "ev-project";
        case "task_completed":
        default:
            return "ev-task";
    }
}
// ISO weekday letter for the small day-bucket header. The week is
// Mon-first throughout the app.
function weekdayShort(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    return date.toLocaleDateString(undefined, { weekday: "short" });
}
// Pull a deep-focus-minutes hint off a task_completed event's
// metadata so the row pill reads "1.5h Deep Focus". Falls back to
// undefined when the data isn't there.
function deepFocusFromMeta(e) {
    const m = e.metadata;
    const raw = m?.deep_focus_minutes;
    if (typeof raw !== "number" || raw <= 0)
        return undefined;
    return Math.round((raw / 60) * 10) / 10;
}
function TodayWeekSummaryCard({ summary, firstName, }) {
    const navigate = useNavigate();
    // Past-week navigation. offset = 0 means the current week (the
    // server-provided `summary` prop). Negative offsets are loaded
    // on-demand from /users/me/week-summary?week_start=... and replace
    // the displayed data while keeping the card chrome stable.
    const [weekOffset, setWeekOffset] = useState(0);
    const [shown, setShown] = useState(summary);
    const [navLoading, setNavLoading] = useState(false);
    const [navError, setNavError] = useState(null);
    // Re-sync to the freshest server payload whenever the parent
    // refetches and we're on the current week. Without this, an
    // optimistic Complete would update the parent feed but the card
    // would still show the stale snapshot.
    useEffect(() => {
        if (weekOffset === 0) {
            setShown(summary);
        }
    }, [summary, weekOffset]);
    const fetchWeek = useCallback(async (offset) => {
        if (offset === 0) {
            setShown(summary);
            return;
        }
        // Compute the Monday for the target week by walking back from
        // this week's Monday. Build the date in local time so the YYYY-
        // MM-DD we send matches the caller's calendar week.
        const today = new Date();
        const thisMonday = new Date(today);
        const dow = (today.getDay() + 6) % 7; // 0=Mon..6=Sun
        thisMonday.setDate(today.getDate() - dow);
        thisMonday.setHours(0, 0, 0, 0);
        const target = new Date(thisMonday);
        target.setDate(thisMonday.getDate() + offset * 7);
        const iso = target.getFullYear() +
            "-" +
            String(target.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(target.getDate()).padStart(2, "0");
        setNavLoading(true);
        setNavError(null);
        try {
            const data = await api.get(`/users/me/week-summary?week_start=${iso}`);
            setShown(data);
        }
        catch (e) {
            setNavError(e instanceof Error ? e.message : "Couldn't load that week.");
        }
        finally {
            setNavLoading(false);
        }
    }, [summary]);
    function go(delta) {
        const next = weekOffset + delta;
        if (next > 0)
            return; // no future weeks
        setWeekOffset(next);
        void fetchWeek(next);
    }
    // Filter tabs above the event list — celebrate solo wins vs co-ships
    // separately. The "with team" count in the stat ribbon and the
    // "team" tab use the same multi-assignee predicate so the headline
    // number always matches what the filter would show.
    const [filter, setFilter] = useState("all");
    const isCoShip = (e) => (e.assignee_names?.length ?? 0) > 1;
    const coShipCount = shown.events.filter(isCoShip).length;
    const filteredEvents = shown.events.filter((e) => {
        if (filter === "solo")
            return !isCoShip(e);
        if (filter === "team")
            return isCoShip(e);
        return true;
    });
    // Pagination — start with the 10 freshest, then +10 per "See more"
    // click. Events come back from the server sorted descending by
    // created_at so a leading slice IS the most-recent window. Reset
    // back to 10 whenever the filter or week changes so a user who
    // expanded "All" doesn't see 30 stale Solo events when they tab
    // over.
    // How many wins each business-unit group shows before its "Show more".
    const PER_BU_PREVIEW = 3;
    // Group state in the wins feed, both keyed by BU name:
    //  • collapsedBUs — the whole group is folded (chevron in the header).
    //  • expandedBUs  — the group shows ALL its wins instead of the first 3.
    const [collapsedBUs, setCollapsedBUs] = useState(new Set());
    const [expandedBUs, setExpandedBUs] = useState(new Set());
    function toggleBU(bu) {
        setCollapsedBUs((prev) => {
            const next = new Set(prev);
            if (next.has(bu))
                next.delete(bu);
            else
                next.add(bu);
            return next;
        });
    }
    function toggleExpandBU(bu) {
        setExpandedBUs((prev) => {
            const next = new Set(prev);
            if (next.has(bu))
                next.delete(bu);
            else
                next.add(bu);
            return next;
        });
    }
    // Every business-unit group starts MINIMIZED — the user expands the ones
    // they care about. Seed collapsedBUs with all current group keys whenever
    // the data set's BU keys change (or filter/week changes). A stable signature
    // of the keys keeps steady-state polling from re-collapsing groups the user
    // just opened; only a genuine change in which BUs are present resets them.
    const buGroupKeys = Array.from(new Set(filteredEvents.map((e) => e.business_unit_name ?? "Other")));
    const buGroupSig = buGroupKeys.slice().sort().join("|");
    useEffect(() => {
        setCollapsedBUs(new Set(buGroupKeys));
        setExpandedBUs(new Set());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, weekOffset, buGroupSig]);
    const showingCurrentWeek = weekOffset === 0;
    const todayIso = new Date().toISOString().slice(0, 10);
    // Group ALL wins into collapsible business-unit chunks with counts. Wins
    // with no BU (personal / BU-less work) land under "Other". Biggest unit
    // leads. Each group previews its first 3 wins with a per-group "Show
    // more"; the header chevron folds the whole group.
    const OTHER_BU = "Other";
    const byBU = new Map();
    for (const e of filteredEvents) {
        const key = e.business_unit_name ?? OTHER_BU;
        const arr = byBU.get(key) ?? [];
        arr.push(e);
        byBU.set(key, arr);
    }
    const grouped = Array.from(byBU.entries())
        .map(([bu, evs]) => ({ bu, events: evs }))
        .sort((a, b) => b.events.length - a.events.length ||
        (a.bu === OTHER_BU ? 1 : b.bu === OTHER_BU ? -1 : a.bu.localeCompare(b.bu)));
    // Max for the mini DF chart's bar scaling.
    const maxDf = Math.max(1, ...shown.deep_focus_by_day.map((d) => d.hours));
    const isFriday = new Date().getDay() === 5;
    // Hero title — celebrate the caller by name on the current week.
    // Past weeks get a more neutral "Last week" / "Week of …" framing
    // since the proud-of-shipping voice only makes sense in-progress.
    // Subtitle: "{N} shipped this week" with pluralization.
    let heroTitle = `Nice week, ${firstName}.`;
    if (weekOffset === -1)
        heroTitle = "Last week recap.";
    else if (weekOffset < -1) {
        const [yy, mm, dd] = shown.week_start.split("-").map(Number);
        const wsDate = new Date(yy, (mm || 1) - 1, dd || 1);
        heroTitle = `Week of ${wsDate.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
        })}`;
    }
    const heroSubtitle = shown.tasks_closed === 0
        ? showingCurrentWeek
            ? "No wins logged yet — close one to kick off the week"
            : "Nothing logged this week"
        : `${shown.tasks_closed} shipped${coShipCount > 0 ? ` · ${coShipCount} with the team` : ""}`;
    return (_jsxs("section", { className: "today-card today-week-summary tsec-week", children: [_jsxs("header", { className: "tws-hero", children: [_jsxs("div", { className: "tws-hero-text", children: [_jsx("h2", { className: "tws-hero-title", children: heroTitle }), _jsx("p", { className: "tws-hero-subtitle", children: heroSubtitle })] }), _jsxs("div", { className: "tws-hero-actions", children: [showingCurrentWeek && shown.streak_days > 0 && (_jsxs("span", { className: "tws-hero-streak", "aria-label": "streak", children: [_jsx("span", { "aria-hidden": "true", children: "\uD83D\uDD25" }), shown.streak_days, "-day streak"] })), _jsx("button", { type: "button", className: "tws-nav-btn", onClick: () => go(-1), disabled: navLoading, "aria-label": "Previous week", title: "Previous week", children: "\u2190" }), _jsx("button", { type: "button", className: "tws-nav-btn", onClick: () => go(1), disabled: navLoading || weekOffset === 0, "aria-label": "Next week", title: "Next week", children: "\u2192" })] })] }), navError && (_jsx("p", { className: "error small tws-nav-error", children: navError })), _jsxs("div", { className: "tws-stat-ribbon", children: [_jsxs("div", { className: "tws-stat tasks", children: [_jsx("span", { className: "tws-stat-value", children: shown.tasks_closed }), _jsx("span", { className: "tws-stat-label", children: "shipped" })] }), _jsxs("div", { className: "tws-stat team", children: [_jsx("span", { className: "tws-stat-value", children: coShipCount }), _jsx("span", { className: "tws-stat-label", children: "with team" })] }), _jsxs("div", { className: "tws-stat df", title: "Submitted Work Hours = Deep Focus blocks + extra minutes you logged when marking tasks complete this week. Matches the Admin team-week column.", children: [_jsxs("span", { className: "tws-stat-value", children: [fmtNum(shown.submitted_work_hours ?? shown.deep_focus_hours), _jsx("span", { className: "tws-stat-unit", children: "h" })] }), _jsx("span", { className: "tws-stat-label", children: "submitted work" }), _jsx("span", { className: "tws-stat-sub", children: showingCurrentWeek ? "Current Week" : "Selected Week" })] }), _jsxs("div", { className: "tws-stat streak", children: [_jsxs("span", { className: "tws-stat-value", children: [shown.streak_days, _jsx("span", { className: "tws-stat-unit", "aria-hidden": "true", children: shown.streak_days > 0 ? "🔥" : "" })] }), _jsxs("span", { className: "tws-stat-label", children: ["day", shown.streak_days === 1 ? "" : "s", " streak"] })] })] }), shown.events.length > 0 && (_jsxs("div", { className: "tws-filter-tabs", role: "tablist", "aria-label": "Filter wins", children: [_jsxs("button", { type: "button", role: "tab", "aria-selected": filter === "all", className: `tws-filter-tab ${filter === "all" ? "active" : ""}`, onClick: () => setFilter("all"), children: ["All", _jsx("span", { className: "tws-filter-count", children: shown.events.length })] }), _jsxs("button", { type: "button", role: "tab", "aria-selected": filter === "solo", className: `tws-filter-tab ${filter === "solo" ? "active" : ""}`, onClick: () => setFilter("solo"), children: ["Solo", _jsx("span", { className: "tws-filter-count", children: shown.events.length - coShipCount })] }), _jsxs("button", { type: "button", role: "tab", "aria-selected": filter === "team", className: `tws-filter-tab ${filter === "team" ? "active" : ""}`, onClick: () => setFilter("team"), children: ["With team", _jsx("span", { className: "tws-filter-count", children: coShipCount })] }), _jsx("button", { type: "button", className: "btn-link tws-filter-momentum", onClick: () => navigate("/momentum"), children: "View full Momentum \u2192" })] })), grouped.length === 0 ? (_jsx("p", { className: "muted small tws-empty", children: shown.events.length === 0
                    ? showingCurrentWeek
                        ? "No wins logged yet this week. Close any task — standard project, one-off, or meeting — and it lands here."
                        : "Nothing logged this week."
                    : filter === "solo"
                        ? "Nothing solo this week — every win was a co-ship."
                        : "No co-ships this week — every win was solo." })) : (_jsx("div", { className: "tws-events", children: grouped.map(({ bu, events }) => {
                    const collapsed = collapsedBUs.has(bu);
                    const expanded = expandedBUs.has(bu);
                    const shownEvents = expanded
                        ? events
                        : events.slice(0, PER_BU_PREVIEW);
                    const moreCount = events.length - shownEvents.length;
                    return (_jsxs("div", { className: "tws-event-group", children: [_jsxs("button", { type: "button", className: "tws-event-bu-head", onClick: () => toggleBU(bu), "aria-expanded": !collapsed, children: [_jsx("i", { className: `ti ti-chevron-${collapsed ? "right" : "down"}`, "aria-hidden": "true" }), _jsx("span", { className: "tws-event-bu-name", children: bu }), _jsx("span", { className: "tws-event-bu-count", children: events.length })] }), !collapsed && (_jsx("ul", { className: "tws-event-list", children: shownEvents.map((e) => {
                                    const hrs = deepFocusFromMeta(e);
                                    // Meetings carry no deep_focus_minutes, but time was still
                                    // spent in them — fall back to the total minutes logged on
                                    // the task so the win card shows "Xh" instead of nothing.
                                    const spentHrs = hrs === undefined &&
                                        typeof e.minutes_spent === "number" &&
                                        e.minutes_spent > 0
                                        ? Math.round((e.minutes_spent / 60) * 10) / 10
                                        : undefined;
                                    // Goal-achieved rows get an "Nd early" pill when the
                                    // event landed before its target_date. Extract the
                                    // metadata into a typed local so TS knows it's a
                                    // number (or undefined) before we render.
                                    let earlyDays;
                                    if (e.event_type === "goal_achieved" && e.metadata) {
                                        const raw = e.metadata.achieved_early_days;
                                        if (typeof raw === "number" && raw > 0) {
                                            earlyDays = raw;
                                        }
                                    }
                                    // "Finished by" credit line — every assignee on the
                                    // underlying task. The actor who clicked Complete is
                                    // not auto-included; if they aren't on the task they
                                    // don't get listed (an admin force-closing someone
                                    // else's work shouldn't earn credit on Momentum).
                                    // Empty for goal_achieved / streak_milestone / etc.
                                    // — those events have no task linkage.
                                    const credits = e.assignee_names ?? [];
                                    return (_jsxs("li", { className: `tws-event ${weekEventCls(e.event_type)}`, children: [_jsx("span", { className: "tws-event-glyph", "aria-hidden": "true", children: weekEventGlyph(e.event_type) }), _jsxs("span", { className: "tws-event-title", children: [e.project_name &&
                                                        !e.title.startsWith(e.project_name) && (_jsxs("span", { className: "tws-event-project", children: [e.project_name, ":", " "] })), e.title] }), hrs !== undefined && (_jsxs("span", { className: "tws-event-pill", children: [fmtNum(hrs), "h Deep Focus"] })), spentHrs !== undefined && (_jsxs("span", { className: "tws-event-pill", title: "Time spent on this", children: [fmtNum(spentHrs), "h"] })), earlyDays !== undefined && (_jsxs("span", { className: "tws-event-pill goal", children: [earlyDays, "d early"] })), credits.length > 0 && (_jsxs("span", { className: "tws-event-credit muted small", children: ["Finished by ", credits.join(", ")] }))] }, e.id));
                                }) })), !collapsed && moreCount > 0 && (_jsxs("button", { type: "button", className: "tws-bu-more", onClick: () => toggleExpandBU(bu), children: ["Show ", moreCount, " more"] })), !collapsed && expanded && events.length > PER_BU_PREVIEW && (_jsx("button", { type: "button", className: "tws-bu-more secondary", onClick: () => toggleExpandBU(bu), children: "Show less" }))] }, bu));
                }) })), _jsxs("div", { className: "tws-footer", children: [_jsxs("div", { className: "tws-df-chart", children: [_jsx("span", { className: "tws-df-label", children: showingCurrentWeek
                                    ? "Deep Focus this week"
                                    : `Deep Focus · ${heroTitle.toLowerCase()}` }), _jsx("div", { className: "tws-df-bars", children: shown.deep_focus_by_day.map((d) => {
                                    const pct = Math.max(4, Math.round((d.hours / maxDf) * 100));
                                    const isToday = showingCurrentWeek && d.day === todayIso;
                                    return (_jsxs("div", { className: `tws-df-bar-wrap ${isToday ? "today" : ""}`, title: `${weekdayShort(d.day)} · ${fmtNum(d.hours)}h`, children: [_jsx("span", { className: "tws-df-bar", style: { height: `${pct}%` } }), _jsx("span", { className: "tws-df-letter", children: weekdayShort(d.day).slice(0, 1) })] }, d.day));
                                }) })] }), isFriday && showingCurrentWeek && (_jsxs("button", { type: "button", className: "tws-reflect", onClick: () => navigate("/momentum"), children: [_jsx("span", { className: "tws-reflect-head", children: "\u2728 Friday reflection" }), _jsx("span", { className: "tws-reflect-prompt", children: "Strong week. One thing you're proud of?" }), _jsx("span", { className: "tws-reflect-cta", children: "Reflect \u2192" })] }))] })] }));
}
